/**
 * PreciseTimer - Event-driven timer with precise time tracking
 * Follows best practices for state management and error handling
 */

export interface TimerState {
  sessionId: string | null;
  totalDuration: number; // in seconds
  startTime: number | null; // timestamp when timer started
  pauseTime: number | null; // timestamp when paused
  isRunning: boolean;
  isPaused: boolean;
  elapsedTime: number; // computed elapsed time
  remainingTime: number; // computed remaining time
  progress: number; // computed progress percentage
  formattedTime: string; // computed formatted time
  lastUpdate: number; // timestamp of last state update
}

export interface TimerEvent {
  type: 'start' | 'pause' | 'resume' | 'stop' | 'complete' | 'error';
  sessionId: string | null;
  timestamp: number;
  data?: any;
}

export class PreciseTimer {
  private state: TimerState;
  private listeners: Set<(state: TimerState) => void> = new Set();
  private eventListeners: Set<(event: TimerEvent) => void> = new Set();
  private updateInterval: NodeJS.Timeout | null = null;
  private errorCount: number = 0;
  private maxErrors: number = 3;
  private isDestroyed: boolean = false;

  constructor() {
    this.state = this.getInitialState();
  }

  private getInitialState(): TimerState {
    return {
      sessionId: null,
      totalDuration: 0,
      startTime: null,
      pauseTime: null,
      isRunning: false,
      isPaused: false,
      elapsedTime: 0,
      remainingTime: 0,
      progress: 0,
      formattedTime: '00:00',
      lastUpdate: Date.now()
    };
  }

  /**
   * Start the timer with a specific duration and session ID
   */
  start(duration: number, sessionId: string): void {
    try {
      if (this.isDestroyed) {
        console.log('🚨 Timer is destroyed, emitting error event');
        this.emitEvent('error', sessionId, { message: 'Timer is destroyed' });
        console.log('🚨 Error event emitted');
        return;
      }

      if (duration <= 0) {
        this.emitEvent('error', sessionId, { message: 'Invalid duration' });
        return;
      }

      // Validate duration is reasonable (1 minute to 4 hours)
      if (duration < 1 || duration > 240) {
        console.error('❌ PreciseTimer: Invalid duration detected:', {
          duration,
          expectedRange: '1-240 minutes'
        });
        this.emitEvent('error', sessionId, { message: 'Duration out of valid range (1-240 minutes)' });
        return;
      }

      console.log('🕐 PreciseTimer: Starting timer', { 
        duration, 
        sessionId, 
        timestamp: new Date().toISOString(),
        callStack: new Error().stack?.split('\n').slice(1, 4).join('\n')
      });
      
      // Stop any existing timer
      this.stopUpdateInterval();
      
      this.state = {
        sessionId,
        totalDuration: duration * 60, // Convert minutes to seconds
        startTime: Date.now(),
        pauseTime: null,
        isRunning: true,
        isPaused: false,
        elapsedTime: 0,
        remainingTime: duration * 60,
        progress: 0,
        formattedTime: this.formatTime(duration * 60),
        lastUpdate: Date.now()
      };

      this.startUpdateInterval();
      this.notifyListeners();
      this.emitEvent('start', sessionId, { duration });
      this.errorCount = 0; // Reset error count on successful start
    } catch (error) {
      this.handleError('start', error);
    }
  }

  /**
   * Pause the timer
   */
  pause(): void {
    try {
      if (this.isDestroyed) {
        this.emitEvent('error', this.state.sessionId, { message: 'Timer is destroyed' });
        return;
      }

      console.log('⏸️ PreciseTimer: Pause called', {
        isRunning: this.state.isRunning,
        isPaused: this.state.isPaused,
        sessionId: this.state.sessionId
      });
      
      if (!this.state.isRunning || this.state.isPaused) {
        console.log('⚠️ PreciseTimer: Cannot pause - timer not running or already paused');
        return;
      }

      console.log('⏸️ PreciseTimer: Pausing timer successfully');
      
      this.state = {
        ...this.state,
        pauseTime: Date.now(),
        isRunning: false,
        isPaused: true,
        lastUpdate: Date.now()
      };

      this.stopUpdateInterval();
      this.notifyListeners();
      this.emitEvent('pause', this.state.sessionId);
    } catch (error) {
      this.handleError('pause', error);
    }
  }

  /**
   * Resume the timer
   */
  resume(): void {
    try {
      if (this.isDestroyed) {
        this.emitEvent('error', this.state.sessionId, { message: 'Timer is destroyed' });
        return;
      }

      console.log('▶️ PreciseTimer: Resume called', {
        isRunning: this.state.isRunning,
        isPaused: this.state.isPaused,
        sessionId: this.state.sessionId
      });
      
      if (!this.state.isPaused || this.state.isRunning) {
        console.log('⚠️ PreciseTimer: Cannot resume - timer not paused or already running');
        return;
      }

      console.log('▶️ PreciseTimer: Resuming timer successfully');
      
      // Calculate the pause duration and adjust start time
      const pauseDuration = Date.now() - (this.state.pauseTime || 0);
      const newStartTime = (this.state.startTime || 0) + pauseDuration;
      
      this.state = {
        ...this.state,
        startTime: newStartTime,
        pauseTime: null,
        isRunning: true,
        isPaused: false,
        lastUpdate: Date.now()
      };

      this.startUpdateInterval();
      this.notifyListeners();
      this.emitEvent('resume', this.state.sessionId);
    } catch (error) {
      this.handleError('resume', error);
    }
  }

  /**
   * Stop the timer
   */
  stop(): void {
    try {
      if (this.isDestroyed) {
        return;
      }

      console.log('🛑 PreciseTimer: Stopping timer');
      
      // Capture sessionId before resetting state
      const sessionId = this.state.sessionId;
      
      this.stopUpdateInterval();
      this.state = this.getInitialState();
      this.notifyListeners();
      this.emitEvent('stop', sessionId);
    } catch (error) {
      this.handleError('stop', error);
    }
  }

  /**
   * Get current elapsed time in seconds
   */
  getElapsedTime(): number {
    if (!this.state.startTime) return 0;
    
    const now = Date.now();
    const pauseTime = this.state.pauseTime || now;
    const endTime = this.state.isPaused ? pauseTime : now;
    
    return Math.floor((endTime - this.state.startTime) / 1000);
  }

  /**
   * Get remaining time in seconds
   */
  getRemainingTime(): number {
    const elapsed = this.getElapsedTime();
    // CRITICAL FIX: Cap elapsed time at totalDuration to prevent negative remaining time
    const cappedElapsed = Math.min(elapsed, this.state.totalDuration);
    return Math.max(0, this.state.totalDuration - cappedElapsed);
  }

  /**
   * Get progress percentage
   */
  getProgress(): number {
    if (this.state.totalDuration === 0) return 0;
    const elapsed = this.getElapsedTime();
    // CRITICAL FIX: Cap progress at 100% to prevent overrun
    const cappedElapsed = Math.min(elapsed, this.state.totalDuration);
    return Math.min(100, (cappedElapsed / this.state.totalDuration) * 100);
  }

  /**
   * Format time as MM:SS
   */
  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Get current state
   */
  getState(): TimerState {
    return { ...this.state };
  }

  /**
   * Check if timer is completed
   */
  isCompleted(): boolean {
    return this.getRemainingTime() <= 0;
  }

  /**
   * Add state change listener
   */
  addListener(listener: (state: TimerState) => void): () => void {
    if (this.isDestroyed) return () => {};
    
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Add event listener
   */
  addEventListener(listener: (event: TimerEvent) => void): () => void {
    if (this.isDestroyed) return () => {};
    
    this.eventListeners.add(listener);
    return () => {
      this.eventListeners.delete(listener);
    };
  }

  /**
   * Preserve current timer state for restoration
   */
  preserveState(): TimerState | null {
    if (this.isDestroyed || !this.state.sessionId) return null;
    
    const elapsed = this.getElapsedTime();
    const remaining = this.getRemainingTime();
    const progress = this.getProgress();
    
    return {
      ...this.state,
      elapsedTime: elapsed,
      remainingTime: remaining,
      progress: progress,
      formattedTime: progress >= 100 ? this.formatTime(elapsed) : this.formatTime(remaining)
    };
  }

  /**
   * Restore timer state from preserved state
   */
  restoreState(preservedState: TimerState): void {
    if (this.isDestroyed) return;
    
    try {
      this.state = {
        ...preservedState,
        lastUpdate: Date.now()
      };
      
      // Check if the restored state is already completed
      if (this.state.startTime && this.state.totalDuration > 0) {
        const elapsedSinceStart = (Date.now() - this.state.startTime) / 1000;
        const totalDurationSeconds = this.state.totalDuration;
        
        if (elapsedSinceStart >= totalDurationSeconds) {
          // Timer is already completed, don't restore
          console.log('⏰ PreciseTimer: Not restoring completed timer state');
          this.stop();
          this.emitEvent('complete', preservedState.sessionId);
          return;
        }
      }
      
      if (this.state.isRunning && !this.state.isPaused) {
        this.startUpdateInterval();
      }
      
      this.notifyListeners();
    } catch (error) {
      this.handleError('restore', error);
    }
  }

  /**
   * Destroy timer and clean up resources
   */
  destroy(): void {
    if (this.isDestroyed) return;
    
    this.stopUpdateInterval();
    // Stop the timer when destroying
    this.state = {
      ...this.state,
      isRunning: false,
      isPaused: false,
      lastUpdate: Date.now()
    };
    // Don't reset state when destroying - keep it for tests
    // this.state = this.getInitialState();
    this.listeners.clear();
    // Don't clear event listeners - they might be needed for error events
    // this.eventListeners.clear();
    this.isDestroyed = true;
  }

  /**
   * Start the update interval
   */
  private startUpdateInterval(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    this.updateInterval = setInterval(() => {
      try {
        this.updateState();
      } catch (error) {
        this.handleError('update', error);
      }
    }, 100); // Update every 100ms for smooth UI
  }

  /**
   * Stop the update interval
   */
  private stopUpdateInterval(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Update timer state
   */
  private updateState(): void {
    // Don't update if timer is not running, is paused, or is destroyed
    if (!this.state.isRunning || this.state.isPaused || this.isDestroyed) return;

    const elapsedTime = this.getElapsedTime();
    const remainingTime = this.getRemainingTime();
    const progress = this.getProgress();
    const formattedTime = this.formatTime(remainingTime);

    // CRITICAL FIX: Check if timer is completed or has overrun
    // Cap elapsed time at totalDuration to prevent overrun
    const cappedElapsedTime = Math.min(elapsedTime, this.state.totalDuration);
    const cappedRemainingTime = Math.max(0, this.state.totalDuration - cappedElapsedTime);
    
    if (remainingTime <= 0 || cappedRemainingTime <= 0) {
      // Log if timer overran
      if (elapsedTime > this.state.totalDuration) {
        console.warn('⚠️ PreciseTimer: Timer overran target duration', {
          elapsed: elapsedTime,
          target: this.state.totalDuration,
          overrun: elapsedTime - this.state.totalDuration,
          sessionId: this.state.sessionId
        });
      }
      
      this.state = {
        ...this.state,
        isRunning: false,
        isPaused: false,
        elapsedTime: this.state.totalDuration, // Cap at target duration
        remainingTime: 0,
        progress: 100,
        formattedTime: this.formatTime(this.state.totalDuration), // Show elapsed time instead of 00:00
        lastUpdate: Date.now()
      };
      
      this.stopUpdateInterval();
      this.notifyListeners();
      this.emitEvent('complete', this.state.sessionId);
      
      // Don't call stop() here as it resets the state
      // The timer is already stopped (isRunning: false)
      return;
    }

    // Update state with capped values to prevent overrun
    this.state = {
      ...this.state,
      elapsedTime: cappedElapsedTime,
      remainingTime: cappedRemainingTime,
      progress: Math.min(100, (cappedElapsedTime / this.state.totalDuration) * 100),
      formattedTime: this.formatTime(cappedRemainingTime),
      lastUpdate: Date.now()
    };

    this.notifyListeners();
  }

  /**
   * Notify all state listeners
   */
  private notifyListeners(): void {
    if (this.isDestroyed) return;
    
    const state = this.getState();
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Timer listener error:', error);
      }
    });
  }

  /**
   * Emit event to all event listeners
   */
  private emitEvent(type: TimerEvent['type'], sessionId: string | null, data?: any): void {
    console.log('📡 emitEvent called:', { type, sessionId, data, isDestroyed: this.isDestroyed });
    
    // Allow error events even when destroyed
    if (this.isDestroyed && type !== 'error') {
      console.log('📡 Blocking non-error event when destroyed');
      return;
    }
    
    const event: TimerEvent = {
      type,
      sessionId,
      timestamp: Date.now(),
      data
    };

    console.log('📡 Emitting event:', event);
    console.log('📡 Event listeners count:', this.eventListeners.size);

    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Timer event listener error:', error);
      }
    });
  }

  /**
   * Handle errors with exponential backoff
   */
  private handleError(operation: string, error: any): void {
    this.errorCount++;
    console.error(`Timer error in ${operation}:`, error);
    
    this.emitEvent('error', this.state.sessionId, { 
      operation, 
      error: error.message,
      errorCount: this.errorCount 
    });

    // Stop timer if too many errors
    if (this.errorCount >= this.maxErrors) {
      console.error('Too many timer errors, stopping timer');
      this.stop();
    }
  }
}

// Export a singleton instance for use throughout the app
export const preciseTimer = new PreciseTimer(); 